import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { gpPercent, priceForMarginPct, toMoney } from '@kdental/shared';

const PRESETS = [25, 26, 30, 40] as const;
const MIN_GP = 5;
const MAX_GP = 70;

/**
 * The "iPod-style" rotary margin dial. Drag the ring (or tap a preset) to set
 * target GP%; the price falls out of cost. Enter a competitor price to see the
 * GP% it implies. Emits the resulting unit price via onPrice.
 */
export function MarginDial({
  cost,
  initialGp = 30,
  onPrice,
}: {
  cost: string;
  initialGp?: number;
  onPrice: (price: string) => void;
}) {
  const [gp, setGp] = useState(initialGp);
  const [competitor, setCompetitor] = useState('');
  const dialRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);

  const price = toMoney(priceForMarginPct(cost, gp));

  useEffect(() => {
    onPrice(price);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gp, cost]);

  const setFromPoint = (clientX: number, clientY: number) => {
    const el = dialRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const deg = (Math.atan2(clientY - (r.top + r.height / 2), clientX - (r.left + r.width / 2)) * 180) / Math.PI;
    const fromTop = (deg + 90 + 360) % 360; // 0 at top, clockwise
    setGp(Math.min(MAX_GP, Math.max(MIN_GP, Math.round((fromTop / 360) * 100))));
  };

  const onDown = (e: ReactPointerEvent) => {
    dragging.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
    setFromPoint(e.clientX, e.clientY);
  };
  const onMove = (e: ReactPointerEvent) => {
    if (dragging.current) setFromPoint(e.clientX, e.clientY);
  };
  const onUp = () => {
    dragging.current = false;
  };

  const clampGp = (n: number) => setGp(Math.min(MAX_GP, Math.max(MIN_GP, n)));
  const onKey = (e: ReactKeyboardEvent) => {
    switch (e.key) {
      case 'ArrowUp':
      case 'ArrowRight':
        clampGp(gp + 1);
        break;
      case 'ArrowDown':
      case 'ArrowLeft':
        clampGp(gp - 1);
        break;
      case 'PageUp':
        clampGp(gp + 5);
        break;
      case 'PageDown':
        clampGp(gp - 5);
        break;
      case 'Home':
        clampGp(MIN_GP);
        break;
      case 'End':
        clampGp(MAX_GP);
        break;
      default:
        return;
    }
    e.preventDefault();
  };

  const rotation = (gp / 100) * 360;
  const compNum = Number(competitor);
  const compGp = competitor !== '' && compNum > 0 ? gpPercent(competitor, cost) : null;
  const compWarn = compGp ? compGp.lt(0) : false;

  return (
    <div className="margin-screen">
      <div className="cost-row">
        <span>Cost ${toMoney(cost)}</span>
        <span>
          Your price <strong style={{ color: '#6FCF8C' }}>${price}</strong>
        </span>
      </div>

      <div
        className="dial-wrap"
        ref={dialRef}
        role="slider"
        tabIndex={0}
        aria-label="Target gross margin percent"
        aria-valuemin={MIN_GP}
        aria-valuemax={MAX_GP}
        aria-valuenow={gp}
        aria-valuetext={`${gp} percent, price $${price}`}
        onKeyDown={onKey}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerLeave={onUp}
      >
        <div className="dial-track" />
        <div className="dial-rotor" style={{ transform: `rotate(${rotation}deg)` }}>
          <div className="dial-knob" />
        </div>
        <div className="dial-inner">
          <div className="gp-label">Gross margin</div>
          <div className="gp-val">
            {gp}
            <span className="pct">%</span>
          </div>
          <div className="your-price">
            Your price <span className="amt">${price}</span>
          </div>
        </div>
      </div>

      <div className="gp-marks">
        {PRESETS.map((p) => (
          <button
            key={p}
            type="button"
            className={`gp-mark${gp === p ? ' active' : ''}`}
            aria-pressed={gp === p}
            onClick={() => setGp(p)}
          >
            {p}%
          </button>
        ))}
      </div>

      <div className="competitor">
        <h4>Competitor price</h4>
        <input
          type="number"
          min="0"
          step="0.01"
          value={competitor}
          onChange={(e) => setCompetitor(e.target.value)}
          placeholder="0.00"
        />
        {compGp && (
          <div className={`result${compWarn ? ' warn' : ''}`}>
            <span>At ${toMoney(competitor)}, your GP is</span>
            <span className="gp">{compGp.toFixed(1)}%</span>
          </div>
        )}
      </div>
    </div>
  );
}
